import { buildFtsMatchQuery } from '@couplespace/core/search';

import { isFtsSupported } from '../db/search-index';
import type { LocalDatabase } from '../db/types';

import type { SearchContainerType, SearchResult } from './api.web';

const CONTAINER_TYPES = new Set<string>(['page', 'database']);
const MAX_ANCESTOR_DEPTH = 64;

type FtsHitRow = {
  block_id: string;
  title: string | null;
  type: string;
  parent_id: string | null;
  bm25_score: number;
  body_snippet: string | null;
  title_snippet: string | null;
};

type BlockAncestorRow = {
  id: string;
  type: string;
  title: string | null;
  parent_id: string | null;
};

function pickSnippet(row: FtsHitRow): string {
  const body = row.body_snippet?.trim();
  if (body) {
    return body;
  }
  return row.title_snippet?.trim() ?? row.title?.trim() ?? '';
}

function isWithinScope(
  blockId: string,
  scopeRootIds: ReadonlySet<string>,
  parentById: Map<string, string | null>,
): boolean {
  if (scopeRootIds.size === 0) {
    return true;
  }
  let current: string | null = blockId;
  const seen = new Set<string>();
  while (current && !seen.has(current)) {
    if (scopeRootIds.has(current)) {
      return true;
    }
    seen.add(current);
    current = parentById.get(current) ?? null;
  }
  return false;
}

async function loadParentMap(database: LocalDatabase): Promise<Map<string, string | null>> {
  const rows = await database.getAllAsync<{ id: string; parent_id: string | null }>(
    `SELECT id, parent_id FROM blocks WHERE deleted_at IS NULL`,
  );
  return new Map(rows.map((row) => [row.id, row.parent_id]));
}

async function resolveContainersForBlocks(
  database: LocalDatabase,
  blockRows: BlockAncestorRow[],
): Promise<
  Map<string, { containerId: string; containerType: SearchContainerType; containerTitle: string }>
> {
  const lookup = new Map(blockRows.map((row) => [row.id, row]));
  const resolved = new Map<
    string,
    { containerId: string; containerType: SearchContainerType; containerTitle: string }
  >();

  async function ensureBlocks(ids: string[]): Promise<void> {
    const missing = ids.filter((id) => !lookup.has(id));
    if (missing.length === 0) {
      return;
    }
    const placeholders = missing.map(() => '?').join(', ');
    const rows = await database.getAllAsync<BlockAncestorRow>(
      `SELECT id, type, title, parent_id FROM blocks WHERE id IN (${placeholders})`,
      ...missing,
    );
    for (const row of rows) {
      lookup.set(row.id, row);
    }
  }

  for (const start of blockRows) {
    let currentId: string | null = start.id;
    let depth = 0;
    while (currentId && depth < MAX_ANCESTOR_DEPTH) {
      if (!lookup.has(currentId)) {
        await ensureBlocks([currentId]);
      }
      const block = lookup.get(currentId);
      if (!block) {
        break;
      }
      if (CONTAINER_TYPES.has(block.type)) {
        resolved.set(start.id, {
          containerId: block.id,
          containerType: block.type as SearchContainerType,
          containerTitle: block.title?.trim() || 'Untitled',
        });
        break;
      }
      currentId = block.parent_id;
      depth += 1;
    }
  }

  return resolved;
}

export async function searchBlocksLocal(
  database: LocalDatabase,
  input: {
    query: string;
    limit?: number;
    scopeRootIds?: string[];
  },
): Promise<SearchResult[]> {
  if (!(await isFtsSupported(database))) {
    return [];
  }

  const match = buildFtsMatchQuery(input.query);
  if (!match) {
    return [];
  }

  const limit = input.limit ?? 30;
  const fetchLimit = Math.min(limit * 8, 200);
  const scopeRoots = new Set(input.scopeRootIds ?? []);

  const rows = await database.getAllAsync<FtsHitRow>(
    `SELECT
      f.block_id AS block_id,
      b.title AS title,
      b.type AS type,
      b.parent_id AS parent_id,
      bm25(block_search_fts) AS bm25_score,
      snippet(block_search_fts, 2, '', '', '…', 32) AS body_snippet,
      snippet(block_search_fts, 1, '', '', '…', 16) AS title_snippet
    FROM block_search_fts f
    INNER JOIN blocks b ON b.id = f.block_id
    WHERE block_search_fts MATCH ?
      AND b.deleted_at IS NULL
    ORDER BY bm25_score
    LIMIT ?`,
    match,
    fetchLimit,
  );

  if (rows.length === 0) {
    return [];
  }

  const parentById = await loadParentMap(database);
  const filtered =
    scopeRoots.size === 0
      ? rows
      : rows.filter((row) => isWithinScope(row.block_id, scopeRoots, parentById));

  const ranked = filtered.slice(0, limit);
  const blockRows: BlockAncestorRow[] = ranked.map((row) => ({
    id: row.block_id,
    type: row.type,
    title: row.title,
    parent_id: row.parent_id,
  }));
  const containers = await resolveContainersForBlocks(database, blockRows);

  return ranked
    .map((row) => {
      const container = containers.get(row.block_id);
      if (!container) {
        return null;
      }
      return {
        blockId: row.block_id,
        title: row.title?.trim() || 'Untitled',
        type: row.type,
        parentId: row.parent_id,
        containerId: container.containerId,
        containerType: container.containerType,
        containerTitle: container.containerTitle,
        snippet: pickSnippet(row),
        score: -row.bm25_score,
        matchedVia: ['fts'],
      };
    })
    .filter((row): row is SearchResult => row !== null);
}
